import ArrowDownIcon from "@/assets/icons/arrow-down.svg";
import CloseIcon from "@/assets/icons/popover-close.svg";
import { useAllowedModels } from "@/hooks/useAllowedModels";
import { Resolution } from "@/types/common";
import { ModelConfig, ModelConfigKey } from "@/types/modelConfig";
import { Button, ConfigProvider, Modal, Select, Spin } from "antd";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";

interface CustomModelModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (config: Partial<Record<ModelConfigKey, string>>) => void;
  initialConfig?: Partial<Record<ModelConfigKey, string>>;
  resolution?: Resolution;
}

const MODEL_CATEGORY_LABELS: Record<string, string> = {
  i2v_model_name: "VIDEO GENERATION MODEL",
  avatar_model_name: "AVATAR GENERATION MODEL",
  image_generation: "IMAGE MODEL",
};

export const CustomModelModal = observer(function CustomModelModal({
  open,
  onClose,
  onConfirm,
  initialConfig = {},
  resolution = Resolution.HD,
}: CustomModelModalProps) {
  const { data, loading, error } = useAllowedModels();
  const [selectedModels, setSelectedModels] =
    useState<Partial<Record<ModelConfigKey, string>>>(initialConfig);

  // Update selected models when initial config or modal opens
  useEffect(() => {
    if (open) {
      setSelectedModels(initialConfig);
    }
  }, [open, initialConfig]);

  const categories = useMemo(() => {
    if (!data) return [];

    const result: Array<{
      key: string;
      label: string;
      models: ModelConfig[];
    }> = [];

    // Add image generation category (combines t2i and i2i)
    // Merge models from both t2i and i2i, keeping track of which keys they support
    if (data.t2i_model_name || data.i2i_model_name) {
      const t2iModels = data.t2i_model_name || [];
      const i2iModels = data.i2i_model_name || [];

      // Create a map to track unique models by display_name
      const modelMap = new Map<string, ModelConfig>();

      // Add all t2i models
      t2iModels.forEach((model) => {
        modelMap.set(model.display_name, model);
      });

      // Add i2i models (will overwrite if same display_name but keep the metadata)
      i2iModels.forEach((model) => {
        if (!modelMap.has(model.display_name)) {
          modelMap.set(model.display_name, model);
        }
      });

      const uniqueModels = Array.from(modelMap.values());

      if (uniqueModels.length > 0) {
        result.push({
          key: "image_generation",
          label: MODEL_CATEGORY_LABELS["image_generation"],
          models: uniqueModels,
        });
      }
    }

    // Add i2v category
    if (data.i2v_model_name && data.i2v_model_name.length > 0) {
      result.push({
        key: "i2v_model_name",
        label: MODEL_CATEGORY_LABELS["i2v_model_name"],
        models: data.i2v_model_name,
      });
    }

    // Add avatar category
    if (data.avatar_model_name && data.avatar_model_name.length > 0) {
      result.push({
        key: "avatar_model_name",
        label: MODEL_CATEGORY_LABELS["avatar_model_name"],
        models: data.avatar_model_name,
      });
    }

    return result;
  }, [data]);

  const handleModelChange = (category: string, modelName: string) => {
    setSelectedModels((prev) => {
      // If image_generation is selected, find the correct model names for t2i and i2i
      if (category === "image_generation" && data) {
        // Find the model in t2i list
        const t2iModel = data.t2i_model_name?.find((m) => m.name === modelName);
        // Find the model in i2i list
        const i2iModel = data.i2i_model_name?.find((m) => m.name === modelName);

        // If model exists in both lists with same name, use it for both
        if (t2iModel && i2iModel && t2iModel.name === i2iModel.name) {
          return {
            ...prev,
            t2i_model_name: modelName,
            i2i_model_name: modelName,
          };
        }

        // Otherwise, find models with matching display_name
        const selectedModel = t2iModel || i2iModel;
        if (selectedModel) {
          const t2iMatch = data.t2i_model_name?.find(
            (m) => m.display_name === selectedModel.display_name
          );
          const i2iMatch = data.i2i_model_name?.find(
            (m) => m.display_name === selectedModel.display_name
          );

          return {
            ...prev,
            t2i_model_name: t2iMatch?.name || modelName,
            i2i_model_name: i2iMatch?.name || modelName,
          };
        }

        // Fallback: set both to the same value
        return {
          ...prev,
          t2i_model_name: modelName,
          i2i_model_name: modelName,
        };
      }

      // For other categories, set normally
      return {
        ...prev,
        [category]: modelName,
      };
    });
  };

  const handleConfirm = () => {
    onConfirm(selectedModels);
  };

  const isResolutionSupported = (model: ModelConfig) => {
    if (!model.price_items || model.price_items.length === 0) {
      return true;
    }

    const hasResolutionPricing = model.price_items.some(
      (item) => !!item.properties?.resolution
    );

    if (!hasResolutionPricing) {
      return true;
    }

    return model.price_items.some(
      (item) => item.properties?.resolution === resolution
    );
  };

  const formatPrice = (model: ModelConfig) => {
    if (!model.price_items || model.price_items.length === 0) {
      return "Price info unavailable";
    }

    const resolutionStr = resolution; // "720p" or "1080p"

    // Find price item matching the current resolution
    let priceItem = model.price_items.find(
      (item) => item.properties?.resolution === resolutionStr
    );

    // If no resolution match found, use first price item with empty properties (default)
    // or the first price item overall as fallback
    if (!priceItem) {
      // Try to find default (empty properties)
      priceItem = model.price_items.find(
        (item) => !item.properties || Object.keys(item.properties).length === 0
      );

      // If still no match, use first price item
      if (!priceItem) {
        priceItem = model.price_items[0];
      }
    }

    const price = priceItem.price.output;
    const unit = priceItem.unit_type;
    const itemResolution = priceItem.properties?.resolution;

    let unitText = unit;

    if (unit === "seconds") {
      unitText = "sec";
    } else if (unit === "images") {
      unitText = "image";
    }

    if (itemResolution) {
      return `${price} Credits/${unitText} (${itemResolution})`;
    }
    return `${price} Credits/${unitText}`;
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      closable={false}
      centered
      maskClosable={true}
      footer={null}
      width={450}
      classNames={{
        content:
          "px-6! py-8! rounded-3xl! bg-[#1A1A24] shadow-[6px_6px_48px_0_rgba(0,0,0,0.5)]!",
      }}
    >
      <div className="relative">
        <CloseIcon
          className="absolute top-1 right-4 w-5 h-5 text-white/60 cursor-pointer hover:text-white"
          onClick={onClose}
          aria-label="Close"
        />

        <h2 className="text-left text-[20px] text-white font-normal mb-6">
          Custom Model Configuration
        </h2>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" />
          </div>
        )}

        {error && (
          <div className="text-center text-red-500 py-8">
            Failed to load model configurations. Please try again.
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
            {categories.map(({ key, label, models }) => (
              <div key={key} className="space-y-2">
                <label className="text-white text-sm font-medium">
                  {label}
                </label>
                <ConfigProvider
                  theme={{
                    components: {
                      Select: {
                        optionHeight: 50,
                        optionSelectedBg: "#26262E",
                        optionPadding: "8px 12px",
                        selectorBg: "rgba(33, 33, 41, 0.70)",
                        controlHeight: 44,
                        borderRadius: 12,
                        colorBorder: "rgba(255, 255, 255, 0.1)",
                        activeBorderColor: "rgba(255, 255, 255, 0.2)",
                        hoverBorderColor: "rgba(255, 255, 255, 0.2)",
                        activeOutlineColor: "none",
                      },
                    },
                  }}
                >
                  <Select
                    className="w-full"
                    placeholder={`Select ${label} model`}
                    value={
                      key === "image_generation"
                        ? selectedModels.t2i_model_name
                        : selectedModels[key as ModelConfigKey]
                    }
                    onChange={(value) => handleModelChange(key, value)}
                    suffixIcon={
                      <ArrowDownIcon className="w-4 h-4 text-white/60" />
                    }
                    options={models
                      .filter((model) => model.is_enabled)
                      .map((model) => {
                        const isVideoCategory = key !== "image_generation";
                        const isDisabled =
                          isVideoCategory && !isResolutionSupported(model);
                        return {
                          label: (
                            <div
                              className={classNames(
                                "flex items-center justify-between text-white h-full",
                                isDisabled ? "opacity-50" : ""
                              )}
                            >
                              <span>{model.display_name}</span>
                              <span className="text-xs text-white/60">
                                {formatPrice(model)}
                              </span>
                            </div>
                          ),
                          value: model.name,
                          disabled: isDisabled,
                        };
                      })}
                  />
                </ConfigProvider>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  controlHeight: 40,
                  fontWeight: 500,
                  fontSize: 14,
                  borderRadius: 24,
                  colorPrimary: "#5251f6",
                  colorPrimaryHover: "#4E4DEA",
                  colorPrimaryActive: "#4241C5",
                },
              },
            }}
          >
            <Button
              type="text"
              className="flex-1 bg-[rgba(255,255,255,0.08)]! text-white! hover:bg-[rgba(255,255,255,0.12)]!"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className="flex-1"
              onClick={handleConfirm}
              disabled={loading || !!error}
            >
              Confirm
            </Button>
          </ConfigProvider>
        </div>
      </div>
    </Modal>
  );
});
